import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, IconButton, Typography, colors } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/WarningOutlined';

const styles = {
  root: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '6px 16px',
    borderRadius: theme.shape.borderRadius
  }),
  default: (theme) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  }),
  success: (theme) => ({
    backgroundColor: colors.green[600],
    color: theme.palette.common.white
  }),
  info: (theme) => ({
    backgroundColor: colors.lightBlue[600],
    color: theme.palette.common.white
  }),
  warning: (theme) => ({
    backgroundColor: colors.yellow[900],
    color: theme.palette.common.white
  }),
  error: (theme) => ({
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText
  }),
  message: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0'
  },
  icon: (theme) => ({
    fontSize: 20,
    marginRight: theme.spacing(1)
  }),
  action: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    paddingLeft: '16px',
    marginRight: '-8px'
  }
};

const icons = {
  default: <InfoIcon />,
  success: <CheckCircleIcon />,
  info: <InfoIcon />,
  warning: <WarningIcon />,
  error: <ErrorIcon />
};

const Alert = forwardRef((props, ref) => {
  const {
    className, icon, variant, message, onClose, ...rest
  } = props;

  return (
    <div>
      <Paper
        {...rest}
        className={className}
        sx={[styles.root, styles[variant]]}
        component={Typography}
        elevation={1}
        ref={ref}
        variant="outlined"
      >
        <Box component="span" sx={styles.icon}>{icon || icons[variant]}</Box>
        <span style={styles.message}>{message}</span>
        {onClose && (
          <IconButton
            sx={styles.action}
            color="inherit"
            key="close"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Paper>
    </div>
  );
});

Alert.displayName = 'Alert';

Alert.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.node,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'info', 'success', 'warning', 'error'])
};

Alert.defaultProps = {
  variant: 'default'
};

export default Alert;
